import type { Invoice } from '@clinic-os/shared/types';

export interface StripePaymentResult {
  success: boolean;
  paymentIntentId?: string;
  checkoutUrl?: string;
  error?: string;
}

export interface PaymentVerifyResult {
  status: string;
  amount: number;
}

export interface PortalSessionResult {
  url: string;
}

const STRIPE_API_BASE = 'https://api.stripe.com/v1';

class StripeServiceImpl {
  private get publishableKey(): string | undefined {
    return import.meta.env['VITE_STRIPE_PUBLISHABLE_KEY'] as string | undefined;
  }

  isConfigured(): boolean {
    return Boolean(this.publishableKey);
  }

  private getAuthHeader(): Record<string, string> {
    // For client-side calls, we use the publishable key
    // The key format is pk_live_... or pk_test_...
    if (!this.publishableKey) {
      throw new Error('Stripe is not configured. Set VITE_STRIPE_PUBLISHABLE_KEY in your environment.');
    }
    return {
      'Authorization': `Bearer ${this.publishableKey}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    };
  }

  /**
   * Create a Stripe Checkout Session for an invoice.
   * This opens Stripe Checkout in redirect mode.
   */
  async createCheckoutSession(invoice: Invoice, clinicSlug: string): Promise<StripePaymentResult> {
    if (!this.isConfigured()) {
      return {
        success: false,
        error: 'Stripe is not configured. Please add VITE_STRIPE_PUBLISHABLE_KEY to your environment.',
      };
    }

    try {
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/${clinicSlug}/invoices/${invoice.id}/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/${clinicSlug}/invoices/${invoice.id}/canceled`;

      // Build line items from invoice items
      const lineItems = invoice.items
        .map(
          (item, index) =>
            `line_items[${index}][name]=${encodeURIComponent(item.description)}&` +
            `line_items[${index}][amount]=${Math.round(item.unitPrice * 100)}&` +
            `line_items[${index}][quantity]=${item.quantity}`
        )
        .join('&');

      const params = new URLSearchParams({
        mode: 'payment',
        success_url: successUrl,
        cancel_url: cancelUrl,
        [`line_items[0][name]`]: `${invoice.invoiceNumber} - Invoice Payment`,
        [`line_items[0][amount]`]: Math.round(invoice.total * 100).toString(),
        [`line_items[0][quantity]`]: '1',
        billing_address_collection: 'required',
        metadata: JSON.stringify({
          invoice_id: invoice.id,
          invoice_number: invoice.invoiceNumber,
        }),
      });

      // Add invoice items as additional line items if present
      if (invoice.items.length > 0) {
        // The first line item is the invoice total, so we include item details in description
        params.set(
          'line_items[0][name]',
          `${invoice.invoiceNumber} - Invoice #${invoice.invoiceNumber} (${invoice.items.length} items)`
        );
      }

      const response = await fetch(`${STRIPE_API_BASE}/checkout/sessions`, {
        method: 'POST',
        headers: this.getAuthHeader(),
        body: params.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || `HTTP ${response.status}`;
        console.error('Stripe checkout session creation failed:', errorMessage);
        return {
          success: false,
          error: `Failed to create checkout session: ${errorMessage}`,
        };
      }

      const session = await response.json();
      return {
        success: true,
        paymentIntentId: session.id,
        checkoutUrl: session.url,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Stripe checkout session error:', errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Create a Stripe Payment Link for an invoice.
   * Payment links are pre-configured payment URLs that can be shared.
   */
  async createPaymentLink(invoice: Invoice, clinicSlug: string): Promise<{ url: string }> {
    if (!this.isConfigured()) {
      throw new Error('Stripe is not configured. Please add VITE_STRIPE_PUBLISHABLE_KEY to your environment.');
    }

    const baseUrl = window.location.origin;

    // Build line items for the payment link
    const lineItems = invoice.items.map((item) => ({
      name: item.description,
      amount: Math.round(item.unitPrice * 100),
      quantity: item.quantity,
    }));

    const params = new URLSearchParams({
      line_items: JSON.stringify(lineItems),
      after_completion_text: `Invoice ${invoice.invoiceNumber} has been paid. Thank you!`,
      metadata: JSON.stringify({
        invoice_id: invoice.id,
        invoice_number: invoice.invoiceNumber,
      }),
    });

    const response = await fetch(`${STRIPE_API_BASE}/payment_links`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: params.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    const paymentLink = await response.json();
    return { url: paymentLink.url };
  }

  /**
   * Verify a payment intent status.
   * Note: This requires a server-side call with secret key for full verification.
   * Client-side only returns basic info from the payment intent ID.
   */
  async verifyPayment(paymentIntentId: string): Promise<PaymentVerifyResult> {
    if (!this.isConfigured()) {
      throw new Error('Stripe is not configured. Please add VITE_STRIPE_PUBLISHABLE_KEY to your environment.');
    }

    const response = await fetch(`${STRIPE_API_BASE}/payment_intents/${paymentIntentId}`, {
      method: 'GET',
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    const paymentIntent = await response.json();
    return {
      status: paymentIntent.status,
      amount: paymentIntent.amount / 100, // Convert from cents
    };
  }

  /**
   * Create a customer portal session for managing subscriptions and payment methods.
   */
  async createPortalSession(customerId: string, returnUrl: string): Promise<PortalSessionResult> {
    if (!this.isConfigured()) {
      throw new Error('Stripe is not configured. Please add VITE_STRIPE_PUBLISHABLE_KEY to your environment.');
    }

    const params = new URLSearchParams({
      customer: customerId,
      return_url: returnUrl,
    });

    const response = await fetch(`${STRIPE_API_BASE}/billing_portal/sessions`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: params.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    const session = await response.json();
    return { url: session.url };
  }
}

export const stripeService = new StripeServiceImpl();
